import type { FC } from 'react';
import { useEffect, useMemo } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';
import { Pressable, StyleSheet } from 'react-native';
import Animated, {
  interpolateColor,
  useAnimatedStyle,
  useSharedValue,
  withTiming
} from 'react-native-reanimated';

import { useAppTheme } from '@/styles/hooks/useAppTheme';

const TRACK_WIDTH = 51;
const TRACK_HEIGHT = 30;
const THUMB_SIZE = 18;
const BORDER_WIDTH = 2;
const THUMB_MARGIN = (TRACK_HEIGHT - THUMB_SIZE) / 2 - BORDER_WIDTH;
const THUMB_TRAVEL =
  TRACK_WIDTH - THUMB_SIZE - (THUMB_MARGIN + BORDER_WIDTH) * 2;
const ANIMATION_DURATION = 180;

const makeStyles = () =>
  StyleSheet.create({
    track: {
      width: TRACK_WIDTH,
      height: TRACK_HEIGHT,
      borderRadius: TRACK_HEIGHT / 2,
      borderWidth: BORDER_WIDTH,
      justifyContent: 'center',
      padding: THUMB_MARGIN
    },
    thumb: {
      width: THUMB_SIZE,
      height: THUMB_SIZE,
      borderRadius: THUMB_SIZE / 2,
      boxShadow: '0 1px 1px rgba(0, 0, 0, 0.2)',
      elevation: 2
    },
    disabled: { opacity: 0.5 }
  });

type Props = {
  value: boolean;
  onValueChange: (value: boolean) => void;
  disabled?: boolean;
  style?: StyleProp<ViewStyle>;
};

export const PillToggle: FC<Props> = ({
  value,
  onValueChange,
  disabled = false,
  style
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(), []);
  const progress = useSharedValue(value ? 1 : 0);

  useEffect(() => {
    progress.value = withTiming(value ? 1 : 0, {
      duration: ANIMATION_DURATION
    });
  }, [value, progress]);

  const trackStyle = useAnimatedStyle(() => ({
    backgroundColor: interpolateColor(
      progress.value,
      [0, 1],
      [theme.colors.outlineVariant, theme.colors.primary]
    ),
    borderColor: interpolateColor(
      progress.value,
      [0, 1],
      [theme.colors.outline, theme.colors.primary]
    )
  }));

  const thumbStyle = useAnimatedStyle(() => ({
    backgroundColor: interpolateColor(
      progress.value,
      [0, 1],
      [theme.colors.outline, theme.colors.onPrimary]
    ),
    transform: [{ translateX: progress.value * THUMB_TRAVEL }]
  }));

  return (
    <Pressable
      accessibilityRole="switch"
      accessibilityState={{ checked: value, disabled }}
      disabled={disabled}
      onPress={() => onValueChange(!value)}
      style={[disabled && styles.disabled, style]}
    >
      <Animated.View style={[styles.track, trackStyle]}>
        <Animated.View style={[styles.thumb, thumbStyle]} />
      </Animated.View>
    </Pressable>
  );
};
